import { useCallback, useEffect, useRef, useState } from 'react';
import { druckenWennBereit } from '@/lib/druckBereit';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useBestellung } from '@/hooks/useBestellung';
import { Spinner } from '@/components/ui/Spinner';
import { ZettelBlatt } from '@/components/druck/ZettelBlatt';
import { SafariUebergabe } from '@/components/druck/SafariUebergabe';
import { useToast } from '@/lib/toast';
import { istIosHomeApp } from '@/lib/iosDruck';
import { useZurueck } from '@/hooks/useZurueck';
import { TeilenDruck } from '@/components/druck/TeilenDruck';
import { blattAlsPdf } from '@/lib/druckTeilen';

// Minimal-Bestellzettel (B3, neu spezifiziert).
//
// Der Zettel ist der Anker zur Bestellung, kein Arbeitsblatt. Deshalb
// steht darauf ausschliesslich, was zum Wiederfinden noetig ist: QR,
// Nummer, Kundenname. Bewusst KEINE Positionen, Checkboxen, Adressen
// oder Freifelder — alles, was zum Draufschreiben einlaedt, erzeugt eine
// zweite Wahrheit neben dem System (Prinzip 4).
//
// Der Dateiname traegt den Unterstrich (`$bestellnr_.druck.tsx`), damit
// die Route NICHT unter der Detailansicht haengt. Ohne ihn ist sie deren
// Kind-Route, und weil die Detailansicht keinen Outlet rendert, blieb der
// Zettel unsichtbar — man sah stattdessen die Detailansicht.
//
// DAS DRUCK-CSS STEHT NICHT MEHR HIER (Befund #46)
//
// Es stand als <style> mitten im React-Baum und blendete nur
// `.nicht-drucken` aus. Beides zusammen war der Befund am iPad: Nach der
// Auswahl des Druckers baute Safari die Seite neu auf und druckte die
// ganze Bestellseite statt des Zettels. Die Regel liegt jetzt in
// web/src/index.css, gilt unabhaengig davon, was React gerade rendert, und
// ist positiv formuliert — gedruckt wird ausschliesslich `.zettel`.
//
// Diese Route setzt dafuer nur EINE Marke: `druck-wurzel` an ihrem
// aeussersten Element. Daran haengen die Regeln — sie steht im Dokument,
// sobald die Route steht, und ist weg, sobald man sie verlaesst. Es gibt
// nichts zu synchronisieren und nichts aufzuraeumen (Befund #51).

function BestellzettelDruck() {
  const { bestellnr } = Route.useParams();
  const navigate = useNavigate();
  const q = useBestellung(bestellnr);
  const gedruckt = useRef(false);
  const geladen = !q.isPending && !q.isError && q.data !== undefined;
  // iPhone-Home-Bildschirm-App: window.print() tut dort seit iOS 27.2
  // nichts mehr. Statt dessen öffnet sich der Zettel in Safari
  // (lib/iosDruck.ts). Einmal beim Einhängen bestimmt — das Gerät
  // wechselt nicht mitten im Druck.
  const [iosApp] = useState(istIosHomeApp);

  // Ein Schritt zurück statt eines neuen Eintrags (hooks/useZurueck.ts):
  // Sonst stünde die Druckseite hinter der Bestellung im Verlauf, und ein
  // späteres „Zurück" aus der Bestellung öffnete den Druck noch einmal.
  const zurueck = useZurueck({ to: '/bestellungen/$bestellnr', params: { bestellnr } });

  // iPhone-App: Sobald Safari übernommen hat, ersetzt die Bestellung die
  // Druckroute im Verlauf. Wer zurück in die App wechselt, steht auf der
  // Bestellung — nicht auf einer Route, die Safari erneut öffnet.
  const toast = useToast();
  const uebergeben = useCallback(() => {
    toast('success', 'In Safari geöffnet — dort drucken.');
    void navigate({ to: '/bestellungen/$bestellnr', params: { bestellnr }, replace: true });
  }, [navigate, bestellnr, toast]);

  // Drucken, sobald etwas auf dem Papier stuende.
  //
  // Frueher stand direkt hinter `window.print()` ein Ruecksprung in die
  // Detailansicht. Das setzt voraus, dass print() blockiert, bis der
  // Nutzer den Dialog geschlossen hat — auf dem Schreibtisch stimmt das,
  // auf iOS nicht. Dort kehrt der Aufruf sofort zurueck, die Navigation
  // rennt der Vorschau davon, und Safari druckt, was danach auf dem
  // Bildschirm steht: die Detailansicht. Der Operator hatte am iPhone
  // eine zweiseitige Vorschau der App statt des Zettels.
  //
  // Jetzt bleibt der Zettel stehen. Zurueck geht es ueber den Knopf, den
  // man sieht — nicht ueber eine Annahme darueber, wann ein Browser
  // etwas tut.
  useEffect(() => {
    if (!geladen || gedruckt.current || iosApp) return;
    gedruckt.current = true;
    // Erst wenn QR-Code und Cover geladen sind (lib/druckBereit.ts).
    return druckenWennBereit('.druck-wurzel', 300);
  }, [geladen, iosApp]);

  // HIER STAND EINE RUECKKEHR AUF `afterprint` (Befund #51)
  //
  // Gedacht war sie als Komfort vom Schreibtisch: Dialog zu, zurueck zur
  // Bestellung. Sie setzt aber voraus, dass `afterprint` erst feuert,
  // wenn die Vorschau fertig ist — wieder eine Annahme darueber, wann ein
  // Browser etwas tut.
  //
  // Im Pruefstand nachgestellt: Feuert `afterprint` frueh, navigiert die
  // Route waehrend der offenen Vorschau zur Detailansicht. Gemessen wurde
  // dann genau das, was der Betreiber am iPad sieht — Pfad auf der
  // Detailseite, Zettel weg, sichtbarer Text die ganze Anwendung, und im
  // erzeugten PDF ZWEI Seiten statt einer.
  //
  // Der Weg zurueck ist der Knopf, den man sieht. Er kostet einen Tipp
  // und kann der Vorschau nicht davonlaufen.

  // iPhone-App: Seit dem Drucktest (29.10.2026) wird über das
  // Teilen-Menü gedruckt (TeilenDruck). Safari bleibt als Ausweg und
  // übernimmt erst, wenn jemand ihn wählt.
  const [ueberSafari, setUeberSafari] = useState(false);
  if (iosApp && ueberSafari) {
    return <SafariUebergabe bestellnr={bestellnr} druckArt="zettel" onZurueck={zurueck} onUebergeben={uebergeben} />;
  }

  if (q.isPending) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }
  if (q.isError || !q.data) {
    return <p className="p-6 text-coral">Bestellung konnte nicht geladen werden.</p>;
  }

  const b = q.data.bestellung;
  return (
    <>
      <div className="druck-wurzel flex min-h-screen flex-col items-center bg-white p-6 text-black">
        {/* Die beiden Knoepfe gehoeren auf den Bildschirm, nicht aufs
            Papier. Sie brauchen dafuer keine eigene Marke mehr: Das
            Druck-CSS zeigt ausschliesslich den Zettel, alles andere in
            dieser Wurzel ist von sich aus stumm. `nicht-drucken` bleibt
            als Beschriftung stehen — sie sagt dem Lesenden, was gemeint
            ist, und der bestehende Test haengt daran.
            "Zurueck zur Bestellung" statt bloss "Zurueck": seit die
            Ansicht nach dem Drucken stehenbleibt, ist er der Weg heraus
            und muss sagen, wohin er fuehrt. */}
        <div className="nicht-drucken mb-6 flex flex-wrap gap-2">
          {iosApp ? (
            <TeilenDruck
              bereit={geladen}
              wurzelSelektor=".druck-wurzel"
              erzeugen={() => zettelAlsPdf(b.bestellnr)}
              onSafari={() => setUeberSafari(true)}
            />
          ) : (
            <button
              type="button"
              onClick={() => window.print()}
              className="min-h-[44px] rounded-md bg-akzent px-4 text-sm text-cream"
            >
              Erneut drucken
            </button>
          )}
          <button
            type="button"
            onClick={zurueck}
            className="min-h-[44px] rounded-md border border-line px-4 text-sm"
          >
            Zurück zur Bestellung
          </button>
        </div>

        <ZettelBlatt b={b} />
      </div>
    </>
  );
}

/**
 * Der Zettel als A4-PDF: die Druckwurzel in Blattbreite, 10 mm Rand,
 * ohne die Knöpfe — dieselbe Gestalt wie im Druck-CSS (index.css).
 */
function zettelAlsPdf(bestellnr: string): Promise<File> {
  const wurzel = document.querySelector<HTMLElement>('.druck-wurzel');
  if (!wurzel) return Promise.reject(new Error('kein Zettel'));
  return blattAlsPdf(wurzel, `Zettel-${bestellnr}.pdf`, (klon) => {
    const w = klon.querySelector<HTMLElement>('.druck-wurzel');
    if (!w) return;
    w.style.width = '210mm';
    w.style.minHeight = '0';
    w.style.padding = '10mm';
    w.querySelectorAll<HTMLElement>('.nicht-drucken').forEach((n) => (n.style.display = 'none'));
  });
}

export const Route = createFileRoute('/bestellungen/$bestellnr_/druck')({
  component: BestellzettelDruck
});
